import { USER_ROLES, normalizeRoleCode } from "../../constants";

export const isSurveyor = (role) => role === USER_ROLES.SRVY;

/** Roles permitted to view the Patients module at all. */
export const canViewPatients = (role) =>
  [
    USER_ROLES.ADMIN,
    USER_ROLES.OA,
    USER_ROLES.RN,
    USER_ROLES.LVN,
    USER_ROLES.CG,
    USER_ROLES.CNA,
    USER_ROLES.PT,
    USER_ROLES.PTA,
    USER_ROLES.OT,
    USER_ROLES.OTA,
    USER_ROLES.ST,
    USER_ROLES.HHA,
    USER_ROLES.MSW,
    USER_ROLES.CON,
    USER_ROLES.SRVY,
  ].includes(role);

/** Demographics (personal/contact/insurance/emergency) — Admin & OA only. */
export const canManageDemographics = (role) =>
  role === USER_ROLES.ADMIN || role === USER_ROLES.OA;

/** Demographics + patient documents (Documents tab) — Admin & OA only. */
export const canManageDocuments = canManageDemographics;

/** Assigning/removing care team staff — same as demographics management. */
export const canAssignStaff = canManageDemographics;

/** Billing actions (sync ledger, create/edit claims) — Admin & OA only, mirrors the Billing sidebar module. */
export const canManageBilling = (role) =>
  role === USER_ROLES.ADMIN || role === USER_ROLES.OA;

/** Docs & notes editing — all patient viewers except Surveyor (read-only). */
export const canEditDocsNotes = (role) =>
  canViewPatients(role) && !isSurveyor(role);

/**
 * Demographics patient notes delete:
 * - Admin / OA: any note
 * - Other editors: only notes they created
 */
export const canDeletePatientNote = (role, noteCreatedBy, currentUserId) => {
  if (!canEditDocsNotes(role)) return false;
  if (role === USER_ROLES.ADMIN || role === USER_ROLES.OA) return true;
  if (noteCreatedBy == null || currentUserId == null) return false;
  return Number(noteCreatedBy) === Number(currentUserId);
};

/** Soft-delete a patient record — disabled; use archive instead. */
export const canDeletePatient = () => false;

/** Archive / unarchive patients — Admin only. */
export const canArchivePatient = (role) =>
  normalizeRoleCode(role) === "ADMIN" || role === USER_ROLES.ADMIN;

/**
 * Archived list row actions. Set `showView: true` to re-enable opening archived records.
 */
export const ARCHIVED_PATIENT_LIST_ACTIONS = {
  showView: false,
  showUnarchive: true,
};

/** Reviewing/statusing clinical forms (To Be Corrected / Completed) — Admin & OA only. */
export const canReviewClinicalForms = (role) => {
  if (role === USER_ROLES.ADMIN || role === USER_ROLES.OA) return true;
  const code = normalizeRoleCode(role);
  return code === "ADMIN" || code === "OA";
};

/** Filling out / submitting clinical forms — same as general docs & notes editing. */
export const canFillClinicalForms = (role) => canEditDocsNotes(role);

/**
 * Deleting a clinical form or a scheduled visit — Admin & OA only. The two are
 * one unit, so this gates both the calendar and the Eval & Orders list.
 */
export const canDeleteClinicalForms = (role) =>
  role === USER_ROLES.ADMIN || role === USER_ROLES.OA;
